import api from './api';

const documentService = {
  // Get all documents with filtering and pagination
  getAll: async (params = {}) => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  // Get document by ID
  getById: async (id) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },

  // Get documents by project
  getByProject: async (projectId, params = {}) => {
    const response = await api.get(`/documents/project/${projectId}`, { params });
    return response.data;
  },

  // Get documents by folder
  getByFolder: async (folderId) => {
    const response = await api.get(`/documents/folder/${folderId}`);
    return response.data;
  },

  // Create new document
  create: async (documentData) => {
    const response = await api.post('/documents', documentData);
    return response.data;
  },

  // Update document
  update: async (id, documentData) => {
    const response = await api.put(`/documents/${id}`, documentData);
    return response.data;
  },

  // Delete document
  delete: async (id) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },

  // Download document
  download: async (id) => {
    const response = await api.get(`/documents/${id}/download`);
    return response.data;
  },

  // Share document
  share: async (id, shareData) => {
    const response = await api.post(`/documents/${id}/share`, shareData);
    return response.data;
  },

  // Get version history
  getVersionHistory: async (id) => {
    const response = await api.get(`/documents/${id}/versions`);
    return response.data;
  },

  // Upload file (helper for file upload)
  uploadFile: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },

  // Helper: Get file type icon
  getFileTypeIcon: (fileType) => {
    if (!fileType) return '📄';
    
    const type = fileType.toLowerCase();
    const icons = {
      // Documents
      'pdf': '📕',
      'doc': '📘',
      'docx': '📘',
      'txt': '📝',
      'rtf': '📝',
      
      // Spreadsheets
      'xls': '📊',
      'xlsx': '📊',
      'csv': '📊',
      
      // Presentations
      'ppt': '📊',
      'pptx': '📊',
      
      // Images
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'svg': '🖼️',
      'bmp': '🖼️',
      
      // Archives
      'zip': '📦',
      'rar': '📦',
      '7z': '📦',
      'tar': '📦',
      'gz': '📦',
      
      // Code
      'js': '💻',
      'jsx': '💻',
      'ts': '💻',
      'tsx': '💻',
      'html': '💻',
      'css': '💻',
      'json': '💻',
      'xml': '💻',
      'py': '💻',
      'java': '💻',
      'cpp': '💻',
      'c': '💻',
      
      // Other
      'mp4': '🎥',
      'avi': '🎥',
      'mov': '🎥',
      'mp3': '🎵',
      'wav': '🎵',
    };
    
    return icons[type] || '📄';
  },

  // Helper: Get file type color
  getFileTypeColor: (fileType) => {
    if (!fileType) return 'bg-gray-100 text-gray-800';
    
    const type = fileType.toLowerCase();
    
    if (['pdf'].includes(type)) {
      return 'bg-red-100 text-red-800';
    }
    if (['doc', 'docx', 'txt', 'rtf'].includes(type)) {
      return 'bg-blue-100 text-blue-800';
    }
    if (['xls', 'xlsx', 'csv'].includes(type)) {
      return 'bg-green-100 text-green-800';
    }
    if (['ppt', 'pptx'].includes(type)) {
      return 'bg-orange-100 text-orange-800';
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp'].includes(type)) {
      return 'bg-purple-100 text-purple-800';
    }
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(type)) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java', 'cpp', 'c'].includes(type)) {
      return 'bg-indigo-100 text-indigo-800';
    }
    
    return 'bg-gray-100 text-gray-800';
  },

  // Helper: Format file size
  formatFileSize: (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  },

  // Helper: Check if file can be previewed
  canPreview: (fileType) => {
    if (!fileType) return false;
    
    const type = fileType.toLowerCase();
    const previewableTypes = [
      'pdf', 'jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp',
      'txt', 'json', 'xml', 'html', 'css', 'js', 'jsx', 'ts', 'tsx'
    ];
    
    return previewableTypes.includes(type);
  },

  // Helper: Get file extension from filename
  getFileExtension: (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  },

  // Helper: Validate file type
  validateFileType: (file, allowedTypes = []) => {
    if (allowedTypes.length === 0) return true;
    
    const extension = documentService.getFileExtension(file.name);
    return allowedTypes.includes(extension);
  },

  // Helper: Validate file size
  validateFileSize: (file, maxSizeMB = 50) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  },

  // Helper: Get common file type categories
  getFileTypeCategories: () => ({
    documents: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
    spreadsheets: ['xls', 'xlsx', 'csv'],
    presentations: ['ppt', 'pptx'],
    images: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'bmp'],
    archives: ['zip', 'rar', '7z', 'tar', 'gz'],
    code: ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java', 'cpp', 'c'],
    media: ['mp4', 'avi', 'mov', 'mp3', 'wav']
  }),

  // Helper: Search documents
  searchDocuments: async (query, filters = {}) => {
    const params = {
      search: query,
      ...filters
    };
    return documentService.getAll(params);
  },

  // Helper: Filter by tags
  filterByTags: async (tags, otherFilters = {}) => {
    const params = {
      tags: Array.isArray(tags) ? tags.join(',') : tags,
      ...otherFilters
    };
    return documentService.getAll(params);
  }
};

// Document Folder Service
export const documentFolderService = {
  // Get all folders
  getAll: async (params = {}) => {
    const response = await api.get('/document-folders', { params });
    return response.data;
  },

  // Get folder by ID
  getById: async (id) => {
    const response = await api.get(`/document-folders/${id}`);
    return response.data;
  },

  // Get folder tree
  getTree: async (projectId = null) => {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get('/document-folders/tree', { params });
    return response.data;
  },

  // Get folders by project
  getByProject: async (projectId) => {
    const response = await api.get(`/document-folders/project/${projectId}`);
    return response.data;
  },

  // Create new folder
  create: async (folderData) => {
    const response = await api.post('/document-folders', folderData);
    return response.data;
  },

  // Update folder
  update: async (id, folderData) => {
    const response = await api.put(`/document-folders/${id}`, folderData);
    return response.data;
  },

  // Delete folder
  delete: async (id, force = false) => {
    const params = force ? { force: 'true' } : {};
    const response = await api.delete(`/document-folders/${id}`, { params });
    return response.data;
  },

  // Helper: Build folder breadcrumb
  buildBreadcrumb: (folder, allFolders) => {
    const breadcrumb = [folder];
    let currentFolder = folder;
    
    while (currentFolder.parent_folder_id) {
      const parent = allFolders.find(f => f.id === currentFolder.parent_folder_id);
      if (parent) {
        breadcrumb.unshift(parent);
        currentFolder = parent;
      } else {
        break;
      }
    }
    
    return breadcrumb;
  },

  // Helper: Get folder icon
  getFolderIcon: (isOpen = false) => {
    return isOpen ? '📂' : '📁';
  }
};

export default documentService;

